import { z } from 'zod';
import {
  BasicQuery,
  EntityReference,
  TimeFrame,
  Metric,
  GraphsData,
  ThresholdFilter,
} from './webapp-common.dto';
import { FlowDeployments } from './webapp-endpoint.dto';

export const QueuePullingType = z.union([
  z.literal('MESSAGE'),
  z.literal('BATCH'),
  z.literal('EVENT'),
]);
export type QueuePullingType = z.infer<typeof QueuePullingType>;

export const MessageQueueTypes = z.union([z.literal('KAFKA'), z.literal('SQS')]);
export type MessageQueueTypes = z.infer<typeof MessageQueueTypes>;

export const MessageQueueMetrics = z.object({
  runs: Metric,
  normalizedRuns: Metric,
  durationAvg: Metric,
  durationP90: Metric,
  errorRate: Metric,
  consumeDurationAvg: Metric,
  consumeDurationP90: Metric,
  msgCount: Metric.optional(),
  normalizedMsgCount: Metric.optional(),
});
export type MessageQueueMetrics = z.infer<typeof MessageQueueMetrics>;

export const MessageQueue = z.object({
  id: z.number(),
  type: MessageQueueTypes,
  queueName: z.string(),
  groupId: z.string().nullable(),
  queuePullingType: QueuePullingType,
  functionId: z.number(),
  services: z.array(EntityReference).length(1), // in reality this will always be a single item array
  firstSeenAt: z.coerce.date(),
  lastSeenAt: z.coerce.date().nullable(),
  metrics: MessageQueueMetrics,
  processingFunction: EntityReference.optional(),
});
export type MessageQueue = z.infer<typeof MessageQueue>;

export const GetMessageQueuesResponse = z.object({
  total: z.number(),
  countFiltered: z.number(), // if no filter is applied, this will be the same as total
  messageQueues: z.array(MessageQueue),
});
export type GetMessageQueuesResponse = z.infer<typeof GetMessageQueuesResponse>;

export const MessageQueueSortableKeys = MessageQueue.pick({
  id: true,
  lastSeenAt: true,
  services: true,
  queueName: true,
  type: true,
})
  .extend({
    'normalizedRuns.value': z.string(),
    'runs.value': z.string(),
    'runs.trend': z.string(),
    'durationAvg.value': z.string(),
    'durationAvg.trend': z.string(),
    'durationP90.value': z.string(),
    'durationP90.trend': z.string(),
    'errorRate.value': z.string(),
    'errorRate.trend': z.string(),
    'consumeDurationAvg.value': z.string(),
    'consumeDurationAvg.trend': z.string(),
    'consumeDurationP90.value': z.string(),
    'consumeDurationP90.trend': z.string(),
    'msgCount.value': z.string(),
  })
  .keyof();
export type MessageQueueSortableKeys = z.infer<typeof MessageQueueSortableKeys>;

export const ErrorBreakdownItem = z.object({
  name: z.string(),
  metrics: z.object({
    count: Metric,
    normalizedCount: Metric,
    distribution: Metric,
  }),
});
export type ErrorBreakdownItem = z.infer<typeof ErrorBreakdownItem>;

export const ErrorBreakdown = z.array(ErrorBreakdownItem);
export type ErrorBreakdown = z.infer<typeof ErrorBreakdown>;

export const GetMessageQueuesQuery = BasicQuery.extend({
  orderBy: z
    .object({
      key: MessageQueueSortableKeys,
      sortOrder: z.union([z.literal('asc'), z.literal('desc')]),
    })
    .optional(),
  serviceIds: z.array(z.coerce.number()).optional(),
  functionIds: z.array(z.coerce.number()).optional(),
  normalizedRuns: ThresholdFilter.optional(),
  durationAvg: ThresholdFilter.optional(),
  errorRate: ThresholdFilter.optional(),
});
export type GetMessageQueuesQuery = z.infer<typeof GetMessageQueuesQuery>;

export const GetMessageQueueQuery = z.object({
  serviceId: z.coerce.number(),
  environmentName: z.string().optional(),
  timeFrame: TimeFrame.optional(),
  tags: z.record(z.string()).optional(),
});
export type GetMessageQueueQuery = z.infer<typeof GetMessageQueueQuery>;

export const GetMessageQueueResponse = z.object({
  messageQueue: MessageQueue,
  graphs: GraphsData,
  deployments: FlowDeployments,
  errors: ErrorBreakdown,
  isMultiGroup: z.boolean(),
});
export type GetMessageQueueResponse = z.infer<typeof GetMessageQueueResponse>;
